import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import RestrictedPostingDateInput from "./RestrictedPostingDateInput";

const FormPopup = ({
  open = false,
  title,
  onClose,
  onSubmit,
  submitText = "Create",
  cancelText = "Cancel",
  submitDisabled = false,
  values = {},
  setValues,
  errors = {},
  touched = {},
  fields = [],
  width = "xl",
  children,
  afterFooter,
  formNoValidate = false,
  bodyGridColumns,
  popupMaxWidth = "1200px",
  popupHeight = "90vh",
}) => {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (!setValues) return;

    const parsedValue =
      type === "checkbox"
        ? checked
        : type === "number"
        ? parseFloat(value) || 0
        : value;

    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setValues((prev) => ({
        ...prev,
        [parent]: {
          ...(prev?.[parent] || {}),
          [child]: parsedValue,
        },
      }));
    } else {
      setValues((prev) => ({
        ...prev,
        [name]: parsedValue,
      }));
    }
  };

  const MultiSelectField = ({ field, value, onChange, gridClass }) => {
    const [openDD, setOpenDD] = useState(false);
    const [tempValue, setTempValue] = useState([]);

    useEffect(() => {
      if (openDD) setTempValue(Array.isArray(value) ? [...value] : []);
    }, [openDD, value]);

    const toggleOption = (optionValue) => {
      const current = Array.isArray(tempValue) ? tempValue : [];
      const exists = current.includes(optionValue);
      const nextValues = exists
        ? current.filter((v) => v !== optionValue)
        : [...current, optionValue];
      setTempValue(nextValues);
    };

    const handleSelectAll = () => {
      const allValues = (field.options || []).map((opt) => opt.value);
      setTempValue(allValues);
    };

    const handleApply = () => {
      onChange(tempValue);
      setOpenDD(false);
    };

    const handleCancel = () => {
      setTempValue(Array.isArray(value) ? [...value] : []);
      setOpenDD(false);
    };

    const selectedOptions = (field.options || []).filter((opt) =>
      (Array.isArray(value) ? value : []).includes(opt.value)
    );

    const displayLabel =
      selectedOptions.length > 0
        ? selectedOptions.map((opt) => opt.label).join(", ")
        : field.placeholder || "Select...";

    const allSelected =
      (field.options || []).length > 0 &&
      tempValue.length === (field.options || []).length;

    return (
      <div className={gridClass}>
        <label className="form-popup-label mb-1.5 block">
          {field.label}
          {field.unit ? ` (${field.unit})` : ""}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </label>

        <div className="relative">
          <button
            type="button"
            onClick={() => setOpenDD((prev) => !prev)}
            disabled={field.disabled}
            className={`form-popup-select-trigger w-full text-left text-sm flex items-center justify-between px-3
            ${field.disabled ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <span
              className={
                selectedOptions.length === 0 ? "text-gray-400 truncate" : "truncate"
              }
            >
              {displayLabel}
            </span>

            <span className="ml-2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-4 w-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </span>
          </button>

          {openDD && !field.disabled && (
            <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-48 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-y-auto">
                <div className="border-b border-gray-200">
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-gray-50 bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      readOnly
                      checked={allSelected}
                      className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-200"
                    />
                    <span className="flex-1 text-left text-xs font-medium text-gray-900">
                      Select All
                    </span>
                  </button>
                </div>

                <ul className="py-0.5 text-xs text-gray-800">
                  {(field.options || []).map((option, index) => {
                    const checked = (Array.isArray(tempValue) ? tempValue : []).includes(
                      option.value
                    );
                    return (
                      <li key={index}>
                        <button
                          type="button"
                          onClick={() => toggleOption(option.value)}
                          className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-gray-50"
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={checked}
                            className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-200"
                          />
                          <span className="flex-1 text-left">{option.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="border-t border-gray-200 p-1.5 flex gap-1.5 flex-shrink-0 bg-white">
                <button
                  type="button"
                  onClick={handleApply}
                  className="flex-1 px-2.5 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700 transition-colors"
                >
                  Apply
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-2.5 py-1.5 bg-gray-200 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  const GroupedMultiSelectField = ({ field, value, onChange, gridClass }) => {
   const [openDD, setOpenDD] = useState(false);
   const [tempValue, setTempValue] = useState([]);
 
   useEffect(() => {
     if (openDD) setTempValue(Array.isArray(value) ? [...value] : []);
   }, [openDD, value]);
 
   const allOptionsFlat = (field.groups || []).flatMap((g) => g.options || []);
   const allValues = allOptionsFlat.map((o) => o.value);
 
   const toggleOption = (optionValue) => {
     const current = Array.isArray(tempValue) ? tempValue : [];
     const exists = current.includes(optionValue);
     const nextValues = exists
       ? current.filter((v) => v !== optionValue)
       : [...current, optionValue];
     setTempValue(nextValues);
   };
 
   const handleSelectAll = () => setTempValue(allValues);
 
   const handleApply = () => {
     onChange(tempValue);
     setOpenDD(false);
   };
 
   const handleCancel = () => {
     setTempValue(Array.isArray(value) ? [...value] : []);
     setOpenDD(false);
   };
 
   const selectedOptions = allOptionsFlat.filter((opt) =>
     (Array.isArray(value) ? value : []).includes(opt.value)
   );
 
   const displayLabel =
     selectedOptions.length > 0
       ? selectedOptions.map((opt) => opt.label).join(", ")
       : field.placeholder || "Select...";
 
   const allSelected = allValues.length > 0 && tempValue.length === allValues.length;
 
   return (
     <div className={gridClass}>
       <label className="form-popup-label mb-1.5 block">
         {field.label}
         {field.required && <span className="ml-1 text-red-500">*</span>}
       </label>
 
       <div className="relative">
         <button
           type="button"
           onClick={() => setOpenDD((prev) => !prev)}
           disabled={field.disabled}
           className={`form-popup-select-trigger w-full text-left text-sm flex items-center justify-between px-3
           ${field.disabled ? "cursor-not-allowed opacity-50" : ""}`}
         >
           <span className={selectedOptions.length === 0 ? "text-gray-400 truncate" : "truncate"}>
             {displayLabel}
           </span>
 
           <span className="ml-2 text-gray-400">
             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
               <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
             </svg>
           </span>
         </button>
 
         {openDD && !field.disabled && (
           <div className="absolute z-50 mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-lg max-h-56 flex flex-col overflow-hidden">
             <div className="flex-1 overflow-y-auto">
               {/* Select all */}
               <div className="border-b border-gray-200">
                 <button
                   type="button"
                   onClick={handleSelectAll}
                   className="flex w-full items-center gap-2 px-3 py-1.5 hover:bg-gray-50 bg-gray-50"
                 >
                   <input
                     type="checkbox"
                     readOnly
                     checked={allSelected}
                     className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-200"
                   />
                   <span className="flex-1 text-left text-xs font-medium text-gray-900">
                     Select All
                   </span>
                 </button>
               </div>
 
               {/* Groups */}
               <div className="py-1">
                 {(field.groups || []).map((group, gi) => (
                   <div key={gi} className="px-2">
                     {/* Group title */}
                     <div className="px-1.5 py-1 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                       {group.title}
                     </div>
 
                     <ul className="pb-1 text-xs text-gray-800">
                       {(group.options || []).map((option, oi) => {
                         const checked = (Array.isArray(tempValue) ? tempValue : []).includes(option.value);
                         return (
                           <li key={oi}>
                             <button
                               type="button"
                               onClick={() => toggleOption(option.value)}
                               className="flex w-full items-center gap-2 px-2.5 py-1.5 hover:bg-gray-50 rounded-lg"
                             >
                               <input
                                 type="checkbox"
                                 readOnly
                                 checked={checked}
                                 className="h-3.5 w-3.5 rounded border-gray-300 text-emerald-600 focus:ring-emerald-200"
                               />
                               <span className="flex-1 text-left">{option.label}</span>
                             </button>
                           </li>
                         );
                       })}
                     </ul>
 
                     {/* Divider between groups */}
                     {gi !== (field.groups || []).length - 1 && (
                       <div className="my-1 border-t border-gray-200" />
                     )}
                   </div>
                 ))}
               </div>
             </div>
 
             {/* Footer buttons */}
             <div className="border-t border-gray-200 p-1.5 flex gap-1.5 flex-shrink-0 bg-white">
               <button
                 type="button"
                 onClick={handleApply}
                 className="flex-1 px-2.5 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700 transition-colors"
               >
                 Apply
               </button>
               <button
                 type="button"
                 onClick={handleCancel}
                 className="flex-1 px-2.5 py-1.5 bg-gray-200 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-300 transition-colors"
               >
                 Cancel
               </button>
             </div>
           </div>
         )}
       </div>
     </div>
   );
 };
  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!onSubmit) return;
    try {
      await onSubmit(values);
    } catch (error) {
      console.error("Error in onSubmit:", error);
    }
  };

  const renderField = (field, fieldIndex) => {
    const fieldValue = field.name?.includes(".")
      ? values?.[field.name.split(".")[0]]?.[field.name.split(".")[1]] ?? ""
      : values?.[field.name] ?? "";

    const fieldError = errors?.[field.name];
    const fieldTouched = touched?.[field.name];
    const showError = fieldError && fieldTouched;

    const gridClasses = {
      "1": "col-span-1",
      "2": "col-span-2",
      "3": "col-span-3",
      full: "fullWidth",
    };
    const gridClass = gridClasses[field.grid] || "col-span-1";

    if (field.type === "multiselect") {
      const handleMultiChange = (newValues) => {
        if (!setValues) return;

        if (field.name.includes(".")) {
          const [parent, child] = field.name.split(".");
          setValues((prev) => ({
            ...prev,
            [parent]: { ...(prev?.[parent] || {}), [child]: newValues },
          }));
        } else {
          setValues((prev) => ({ ...prev, [field.name]: newValues }));
        }
      };

      return (
        <MultiSelectField
          key={fieldIndex}
          field={field}
          value={Array.isArray(fieldValue) ? fieldValue : []}
          onChange={handleMultiChange}
          gridClass={gridClass}
        />
      );
    }
    if (field.type === "grouped_multiselect") {
     const handleMultiChange = (newValues) => {
       if (!setValues) return;
       if (field.name.includes(".")) {
         const [parent, child] = field.name.split(".");
         setValues((prev) => ({
           ...prev,
           [parent]: { ...(prev?.[parent] || {}), [child]: newValues },
         }));
       } else {
         setValues((prev) => ({ ...prev, [field.name]: newValues }));
       }
     };
   
     return (
       <GroupedMultiSelectField
         key={fieldIndex}
         field={field}
         value={Array.isArray(fieldValue) ? fieldValue : []}
         onChange={handleMultiChange}
         gridClass={gridClass}
       />
     );
   }
    // ✅ YES/NO control (new field type)
if (field.type === "yesno") {
    return (
      <div key={fieldIndex} className={gridClass}>
        <label className="form-popup-label mb-1.5 block">
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </label>

     <div className="flex items-center gap-6">
       <label className="flex items-center gap-3 cursor-pointer">
         <input
           type="checkbox"
           checked={!!fieldValue}
           onChange={() => {
             const e = { target: { name: field.name, type: "checkbox", checked: true } };
             handleInputChange(e);
           }}
           className="h-6 w-6 rounded-md border-gray-300 text-emerald-700 focus:ring-2 focus:ring-emerald-200"
         />
         <span className="text-sm text-gray-900">Yes</span>
       </label>

       <label className="flex items-center gap-3 cursor-pointer">
         <input
           type="checkbox"
           checked={!fieldValue}
           onChange={() => {
             const e = { target: { name: field.name, type: "checkbox", checked: false } };
             handleInputChange(e);
           }}
           className="h-6 w-6 rounded-md border-gray-300 text-emerald-700 focus:ring-2 focus:ring-emerald-200"
         />
         <span className="text-sm text-gray-900">No</span>
       </label>
     </div>
   </div>
 );
}

    // ✅ Custom field type for rendering custom JSX
    if (field.type === "custom" && field.render) {
      return (
        <div key={fieldIndex} className={gridClass}>
          {field.render()}
        </div>
      );
    }

    if (field.type === "date" && typeof field.isDateSelectable === "function") {
      return (
        <RestrictedPostingDateInput
          key={fieldIndex}
          field={field}
          value={fieldValue}
          onChange={handleInputChange}
          showError={showError}
          fieldError={fieldError}
          gridClass={gridClass}
        />
      );
    }

    return (
      <div key={fieldIndex} className={gridClass}>
        <label className="form-popup-label mb-1.5 block">
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </label>

        {field.type === "select" ? (
          <div className="relative">
            <select
              name={field.name}
              value={fieldValue}
              onChange={handleInputChange}
              disabled={field.disabled}
              className={`w-full appearance-none pr-10 ${field.disabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {field.placeholder && <option value="">{field.placeholder}</option>}
              {field.options?.map((option, optIndex) => (
                <option key={optIndex} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <div className="pointer-events-none absolute right-5 top-1/2 -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-5 w-5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                />
              </svg>
            </div>
          </div>
        ) : field.type === "checkbox" ? (
          // ✅ Screenshot-like checkbox row
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              name={field.name}
              checked={!!fieldValue}
              onChange={handleInputChange}
              disabled={field.disabled}
              className={`h-5 w-5 rounded-md border-gray-300 text-emerald-600 focus:ring-2 focus:ring-emerald-200 ${
                field.disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
              }`}
            />
            <span className="text-sm text-gray-800">
              {field.checkboxLabel || field.label}
            </span>
          </div>
        ) : (
          <input
            type={field.type || "text"}
            name={field.name}
            value={fieldValue}
            onChange={handleInputChange}
            onKeyDown={
              field.strictNumeric
                ? (e) => {
                    const isNumber = e.key >= "0" && e.key <= "9";
                    const isControl = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key);
                    const isDot = e.key === ".";
                    const isMinusAtStart =
                      field.allowNegative &&
                      e.key === "-" &&
                      e.currentTarget.selectionStart === 0 &&
                      !String(e.currentTarget.value || "").includes("-");

                    if (!isNumber && !isControl && !isDot && !isMinusAtStart) {
                      e.preventDefault();
                    }
                  }
                : undefined
            }
            onPaste={
              field.strictNumeric
                ? (e) => {
                    const pasted = e.clipboardData?.getData("text") || "";
                    const numericPattern = field.allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
                    if (!numericPattern.test(pasted.trim())) e.preventDefault();
                  }
                : undefined
            }
            placeholder={field.placeholder}
            disabled={field.disabled}
            min={field.min}
            max={field.max}
            step={field.step}
            required={field.required}
            className={`w-full ${field.disabled ? "cursor-not-allowed opacity-50" : ""} ${
              showError ? "!border-red-500 focus:!ring-red-200" : ""
            }`}
          />
        )}

        {showError && <p className="mt-2 text-sm text-red-500">{fieldError}</p>}
      </div>
    );
  };

  const renderGroup = (group, groupIndex) => {
    return (
      <div
        key={groupIndex}
        className="form-popup-section-card mt-6"
      >
        <h3 className="form-popup-section-title">{group.title}</h3>

        <div className="form-popup-section-grid">
          {group.fields?.map((field, fieldIndex) =>
            renderField(field, fieldIndex)
          )}
        </div>
      </div>
    );
  };

  if (!open) return null;

  const gridColsClass = bodyGridColumns != null ? ` form-popup-cols-${bodyGridColumns}` : "";
  const popupContent = (
    <div
      className={`form-popup-modal fixed inset-0 flex items-center justify-center p-3 md:p-4${gridColsClass}`}
      style={{ zIndex: 99999 }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="formpopup-title"
    >
      <div
        className="form-popup-overlay absolute inset-0"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-full flex items-stretch justify-center">
      <div
  className="form-popup-container mx-auto w-full flex flex-col overflow-visible"
  style={{ maxWidth: popupMaxWidth, height: "100%", maxHeight: "100%" }}
  onClick={(e) => e.stopPropagation()}
>
          <div className="flex items-center justify-between">
            <h2
              id="formpopup-title"
              className="form-popup-title"
            >
              {title}
            </h2>

            <button
              type="button"
              onClick={onClose}
              className="form-popup-close"
              aria-label="Close modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <hr className="form-popup-divider" />

          <form onSubmit={handleSubmit} noValidate={formNoValidate} className="h-full flex flex-col min-h-0">
            <div className="pt-2 flex-1 overflow-y-auto min-h-0">
              <div className="fpBody">
                {fields.map((field, index) => {
                  if (field.type === "group") {
                    return (
                      <div
                        key={`group-${index}`}
                        className="fullWidth"
                        data-full="true"
                      >
                        {renderGroup(field, index)}
                      </div>
                    );
                  }
                  return renderField(field, index);
                })}
                {children != null && (
                  <div className="fullWidth" data-full="true">
                    {children}
                  </div>
                )}
                {afterFooter != null && (
                  <div className="fullWidth" data-full="true">
                    {afterFooter}
                  </div>
                )}
              </div>
            </div>
            <div className="form-popup-footer border-t border-[#EEF1F5] bg-white px-2 py-3 flex items-center justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="form-popup-btn-outline"
              >
                {cancelText}
              </button>

              {submitText && (
                <button
                  type="submit"
                  className="form-popup-btn-solid"
                  disabled={submitDisabled}
                >
                  {submitText}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return createPortal(popupContent, document.body);
};

export default FormPopup;
